import type { BadgeColor } from "@/components/ui/badge/Badge";

/** User-facing mission lifecycle labels (`MissionSummary.status`). */
export const MISSION_STATUS_DISPLAY: Record<string, string> = {
  active: "Active",
  completed: "Completed",
  paused: "Paused",
  draft: "Draft",
  cancelled: "Cancelled",
  archived: "Archived",
};

export const MISSION_STATUS_BADGE_COLOR: Record<string, BadgeColor> = {
  active: "green",
  completed: "emerald",
  paused: "amber",
  draft: "zinc",
  cancelled: "red",
  archived: "slateMuted",
};

/** Tailwind `bg-*` classes for vertical accent bars (aligned with meetings legend). */
const MISSION_STATUS_ACCENT_BG: Record<string, string> = {
  active: "bg-emerald-400",
  completed: "bg-emerald-500",
  paused: "bg-amber-500",
  draft: "bg-slate-500",
  cancelled: "bg-red-400",
  archived: "bg-slate-600",
};

export function getMissionStatusAccentBarBgClass(
  status: string | null | undefined,
): string {
  if (status == null || status === "") return "bg-slate-500/70";
  const key = status.trim().toLowerCase();
  return MISSION_STATUS_ACCENT_BG[key] ?? "bg-slate-500/70";
}

export function getMissionRowAccentBarBgClass(mission: {
  status: string;
  attention: { needs_attention: boolean };
}): string {
  if (mission.attention.needs_attention) return "bg-amber-400";
  return getMissionStatusAccentBarBgClass(mission.status);
}

export function getMissionsStatusFilterAccentBarBgClass(
  filter: "needs_attention" | "active" | "completed" | "all",
): string {
  if (filter === "needs_attention") return "bg-amber-400";
  if (filter === "active") return "bg-emerald-400";
  if (filter === "completed") return "bg-emerald-500";
  return "bg-slate-500/70";
}

function humanizeUnknownStatus(status: string): string {
  const s = status.trim();
  if (!s) return "Unknown";
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatMissionStatusLabel(status: string | null | undefined): string {
  if (status == null || status === "") return "Unknown";
  const key = status.trim().toLowerCase();
  return MISSION_STATUS_DISPLAY[key] ?? humanizeUnknownStatus(status);
}

export function getMissionStatusBadgeColor(
  status: string | null | undefined
): BadgeColor {
  if (status == null || status === "") return "zinc";
  const key = status.trim().toLowerCase();
  return MISSION_STATUS_BADGE_COLOR[key] ?? "zinc";
}
